import React, { useState, useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { SimplePageList } from './SimplePageList';
import { useAuth } from '../../context/AuthContext';
import { cn } from '../../utils/cn';
import { Menu } from 'lucide-react';
import { VideoChatNotificationBubble } from '../notifications/VideoChatNotificationBubble';
import FloatingChatWindow from '../finChats/FloatingChatWindow';
import { ClaireBubble } from '../claire/ClaireBubble';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../lib/firebase/config';
import { UserRole } from '../../lib/types/auth';
import NoInternetError from '../error/NoInternetError';
import { useLayoutProfile } from '../../hooks/useLayoutProfile';
import { isPathExcluded } from '../../lib/access/pageExclusions';
import MetalPricesBar from '../inventory/MetalPricesBar';
import HelpButton from '../help/HelpButton';
import { HelpRequestListener } from '../help/HelpRequestListener';
import { useStoreViewActive } from '../../hooks/useStoreViewActive';
import RunningTimerWidget from '../task/RunningTimerWidget';
import { LoadingBars } from '../LoadingScreen';
import { SoundboardLauncher } from '../soundboard/SoundboardLauncher';
import { SoundboardListener } from '../soundboard/SoundboardListener';
import { SessionRefreshListener } from '../system/SessionRefreshListener';
import { GlanceTargetListener } from '../glance/GlanceTargetListener';
import { InstantConnectListener } from '../instant-connect/InstantConnectListener';
import { InstantConnectModal } from '../instant-connect/InstantConnectModal';

export const Layout: React.FC = () => {
  const { currentUser, loading } = useAuth();

  // Store-only users (pure Store Manager/Staff accounts, or anyone previewing
  // via the sidebar "Store View" toggle) get the Help button instead of the
  // live metal-prices bar.
  const storeViewActive = useStoreViewActive();
  const userRoles = currentUser?.roles || [];
  const isMasterAdminUser = userRoles.includes(UserRole.MasterAdmin);
  const hasStoreRole = userRoles.some(
    (r) => r === UserRole.StoreManager || r === UserRole.StoreStaff
  );
  const hasNonStoreRole = userRoles.some(
    (r) => r !== UserRole.StoreManager && r !== UserRole.StoreStaff
  );
  const isPureStoreUser = !isMasterAdminUser && hasStoreRole && !hasNonStoreRole;
  const isStoreOnlyUser = storeViewActive || isPureStoreUser;

  const { profile, isSidebar, isLoading: profileLoading } = useLayoutProfile();
  const location = useLocation();
  const hideSidebarRoutes = ['/brainstorm', '/live-sales-dashboard', '/live-seller-view', '/brand-guidelines'];

  // Portrait mode on the task calendar reclaims the sidebar's width for a
  // rotated monitor. Scoped to /tasks so the chrome returns everywhere else.
  const [taskPortraitMode, setTaskPortraitMode] = useState(
    () => localStorage.getItem('task_portrait_mode') === '1'
  );

  const shouldHideSidebar =
    hideSidebarRoutes.some(route => location.pathname.startsWith(route)) ||
    (taskPortraitMode && location.pathname.startsWith('/tasks'));

  const isDashboardRoute =
    location.pathname === '/' || location.pathname === '/dashboard';

  const [expanded, setExpanded] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [infraOpStatus, setInfraOpStatus] = useState<boolean>(false);
  const [icModalOpen, setIcModalOpen] = useState(false);
  const [metalPricesBarVisible, setMetalPricesBarVisible] = useState(() => {
    const saved = localStorage.getItem('metalPricesBarVisible');
    return saved !== 'false';
  });

  // Listen for Instant Connect open/close events from the sidebar
  useEffect(() => {
    const handler = () => setIcModalOpen(true);
    window.addEventListener('open-instant-connect', handler);
    return () => window.removeEventListener('open-instant-connect', handler);
  }, []);

  // Task calendar broadcasts its portrait toggle so the sidebar can step aside.
  useEffect(() => {
    const handler = (e: Event) =>
      setTaskPortraitMode(!!(e as CustomEvent<boolean>).detail);
    window.addEventListener('task-portrait-mode-changed', handler);
    return () => window.removeEventListener('task-portrait-mode-changed', handler);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) setMobileSidebarOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const settingsRef = doc(db, 'settings', 'system');
    const unsubscribe = onSnapshot(settingsRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        const newInfraOpStatus = data.infraOpStatus || false;
        setInfraOpStatus(newInfraOpStatus);
      }
    });

    return unsubscribe;
  }, []);

  if (loading || profileLoading) {
    // Universal loader: black equalizer bars on the warm tan surface, same as
    // /intro and every Suspense fallback so the transition feels continuous.
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: '#f0e6d2' }}>
        <LoadingBars size="md" />
      </div>
    );
  }

  if (!currentUser) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (infraOpStatus && !currentUser.roles?.includes(UserRole.IM)) {
    return <NoInternetError />;
  }

  // Render the appropriate navigation based on layout profile
  const renderNavigation = () => {
    if (shouldHideSidebar) return null;

    if (isSidebar) {
      return (
        <div className="fixed top-0 left-0 h-full z-50">
          
          <Sidebar
            expanded={expanded}
            setExpanded={setExpanded}
            isMobile={isMobile}
            isMobileOpen={mobileSidebarOpen}
            onClose={() => setMobileSidebarOpen(false)}
          />
        </div>
      );
    }

    // Simple list layout
    return (
      <div className="fixed top-0 left-0 h-full z-50">
        <SimplePageList
          profile={profile}
          isMobile={isMobile}
          isMobileOpen={mobileSidebarOpen}
          onClose={() => setMobileSidebarOpen(false)}
        />
      </div>
    );
  };

  return (
    <div className={cn(
      'flex min-h-screen relative',
      isDashboardRoute ? 'bg-[#faf6ec]' : 'bg-white'
    )}>
      {/* Render navigation based on layout profile */}
      {renderNavigation()}

      <div
        className={cn(
          'flex-1 flex flex-col transition-all duration-300 min-w-0 overflow-x-hidden overflow-y-auto h-screen',
          // Only add left padding if navigation is visible
          shouldHideSidebar 
            ? 'pl-0' 
            : isMobile 
              ? 'pl-0' 
              : isSidebar
                ? 'pl-[256px]'
                : 'pl-[280px]'
        )}
      >
        {/* Toggle button on mobile - only show if sidebar is enabled */}
        {isMobile && !shouldHideSidebar && (
          <button
            onClick={() => setMobileSidebarOpen(true)}
            className="p-4 text-gray-700 fixed top-4 left-4 z-40"
          >
            <Menu />
          </button>
        )}

        <main 
          className={cn(
            'p-0 w-full max-w-full transition-all duration-300',
            isDashboardRoute ? 'bg-[#faf6ec]' : 'bg-white',
            isMobile && !shouldHideSidebar && 'pt-20',
            !isMobile && !shouldHideSidebar && 'pl-4'
          )}
          style={{ 
            minHeight: '100%',
            paddingBottom: !isStoreOnlyUser && metalPricesBarVisible ? '80px' : '16px' 
          }}
        >
          {isPathExcluded(location.pathname, currentUser.excludedPages) ? (
            <div className="flex items-center justify-center min-h-[60vh] p-6">
              <div className="text-center max-w-md">
                <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m0 0v2m0-2h2m-2 0H10m2-5V9m0 0V7m0 2h2m-2 0H10M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Access Restricted</h3>
                <p className="text-gray-600">This page has been disabled for your account. Contact an administrator if you need access.</p>
              </div>
            </div>
          ) : (
            <Outlet />
          )}
        </main>

        {/* Video Chat Notification Bubble */}
        <VideoChatNotificationBubble 
          className="right-6"
        />

        {/* Floating Chat Window */}
        <FloatingChatWindow />

        {/* Claire assistant — renders only for users with claireAccess (or Master Admin) */}
        <ClaireBubble meltBar={isStoreOnlyUser ? 'none' : metalPricesBarVisible ? 'open' : 'closed'} />

        {/* Soundboard launcher (top-left, offset past the sidebar) +
            invisible broadcast listener. Both gate themselves on
            currentUser.soundboardAccess so they render no-ops for users
            without access. */}
        <SoundboardLauncher
          leftOffset={
            shouldHideSidebar
              ? 8
              : isMobile
                ? 60
                : isSidebar
                  ? 264
                  : 288
          }
        />
        <SoundboardListener />
        <SessionRefreshListener />
        <GlanceTargetListener />
        <InstantConnectListener />
        <InstantConnectModal open={icModalOpen} onClose={() => setIcModalOpen(false)} />

        {/* Running focus-timer widget (bottom-left, clears the sidebar) */}
        <RunningTimerWidget
          leftOffset={
            shouldHideSidebar
              ? 12
              : isMobile
                ? 12
                : isSidebar
                  ? 268
                  : 292
          }
        />

        {/* Store-only users get a Help button instead of the metal-prices bar.
            Everyone else keeps the live spot-price bar. */}
        {isStoreOnlyUser ? (
          <HelpButton
            leftOffset={
              shouldHideSidebar
                ? 12
                : isMobile
                  ? 12
                  : isSidebar
                    ? 268
                    : 292
            }
          />
        ) : (
          <MetalPricesBar 
            leftOffset={
              shouldHideSidebar 
                ? 0 
                : isMobile 
                  ? 0 
                  : isSidebar
                    ? 256
                    : 280
            }
            isVisible={metalPricesBarVisible}
            onVisibilityChange={setMetalPricesBarVisible}
            darkMode={false}
          />
        )}

        {/* Help-request popups + ding for designated responders (self-gates). */}
        <HelpRequestListener />
      </div>
    </div>
  );
};
